import React from 'react'

/**
 * ScreenShell — uniform screen chrome for every routed page.
 *
 * Zone grammar (ops-console pattern, established by InboundInterchange):
 *   header strip (title / subtitle + right-side context + actions)
 *   workspace    (children fill remaining height)
 *   footer       (optional action bar — primary workflow buttons)
 *
 * Card recipe inside the workspace: `rounded-lg border border-subtle bg-card`
 * with section labels `text-xs font-semibold uppercase tracking-wide
 * text-muted-foreground`.
 */

export interface ScreenShellProps {
  title: string
  subtitle?: string
  /** Right-aligned status chips — snapshot age, draft state, connection. */
  context?: React.ReactNode
  /** Right-aligned controls — e.g. ScenarioControls. */
  actions?: React.ReactNode
  /** Footer action bar — Preview / Dispatch / Print etc. */
  footer?: React.ReactNode
  children: React.ReactNode
}

const ScreenShell: React.FC<ScreenShellProps> = ({
  title,
  subtitle,
  context,
  actions,
  footer,
  children,
}) => (
  <div className="flex flex-col h-full overflow-hidden bg-background text-foreground">
    <header className="flex items-center justify-between gap-4 px-4 py-2 border-b border-border bg-main">
      <div className="min-w-0">
        <div className="font-bold text-sm text-accent-blue whitespace-nowrap">{title}</div>
        {subtitle && (
          <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {context}
        {actions}
      </div>
    </header>
    <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
    {footer && (
      <footer className="flex items-center justify-end gap-2 px-4 py-2 border-t border-border bg-main">
        {footer}
      </footer>
    )}
  </div>
)

export default React.memo(ScreenShell)
